import { inject, Injectable } from '@angular/core'
import { Router, type ActivatedRouteSnapshot, type RouterStateSnapshot } from '@angular/router'
import { LocaleService } from '@core/locale/locale.service'
import { RoleService } from '@core/role/role.service'
import { SnackbarService } from '@core/snackbar/snackbar.service'
import { IS_SET } from '@core/util/check.util'
import { TO_NUMBER } from '@core/util/convert.util'
import { ENV_DEMO_BYPASS_AUTH } from '@core/util/env.util'
import { TO_STRING } from '@core/util/string.util'
import { AuthService } from './auth.service'

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  SnackbarService = inject(SnackbarService)
  AuthService = inject(AuthService)
  Router = inject(Router)
  LocaleService = inject(LocaleService)
  RoleService = inject(RoleService)

  currentUser: any

  async canActivate (_: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    // simplified UI : a feature hidden from the active role is not reachable by URL either,
    // the user is sent back to the home page of their role
    if (!this.RoleService.isPathAllowed(state.url)) {
      await this.Router.navigateByUrl(this.RoleService.currentRoleWS().homePath)
      return false
    }

    // demo build : skip authentication entirely so the UI is viewable without a backend login
    if (ENV_DEMO_BYPASS_AUTH) {
      return true
    }

    let bAllow = false

    let self: any

    if (this.AuthService.checkJwt()) {
      await this.AuthService.refreshCachedSelf()
      self = this.AuthService.getCachedSelf()
      bAllow = IS_SET(self)

      const currentUser = this.AuthService.getCachedSelf()
      this.currentUser = currentUser

      const currentUserRolesBitmap = TO_NUMBER(currentUser?.usRolesBitmap)

      const bCurrentUserIsResellersAdmin = currentUserRolesBitmap === 3

      if (bCurrentUserIsResellersAdmin) {
        const allowedLinkList = ['/top-up', '/resellers']
        bAllow = allowedLinkList.includes(state.url)
      }

      const bCurrentUserIsAgent = currentUserRolesBitmap === 4

      if (bCurrentUserIsAgent) {
        const allowedLinkList = ['/terminals-nav', '/terminals-bus']
        bAllow = allowedLinkList.includes(state.url)
      }
    }

    if (!bAllow) {
      this.SnackbarService.showInfo('Session expired')
      this.AuthService.logout({ origin: state.url })
      await this.Router.navigateByUrl('/login')
    } else {
      if (this.LocaleService.localeBS.value !== self.usLocaleText) {
        this.LocaleService.localeBS.next(TO_STRING(self.usLocaleText))
      }
    }

    return bAllow
  }
}
